import { useId } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';

import { LatestViolationFix } from '@/features/rules';
import { SCREEN_PATHS } from '@/shared/routes';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

import type { FactorImpact, RuleFactor, ScoreFactors } from '../api/scoring';
import {
  arithmetic,
  checkSentence,
  FACTORS_SHOWN,
  filterFactors,
  fixRanks,
  helping,
  hurting,
  largestSlice,
  passLine,
  percent,
  points,
  SEARCH_FROM,
} from '../model/factors';
import type {
  Arithmetic,
  CheckSentence,
  FactorFilter,
  FactorView,
} from '../model/factors';

/* The checks behind a score: which are costing points, most first, and which are holding it up.
 *
 * **The number on a costly row is the order to fix them in**, because the list is the API's own
 * ordering by points lost. A passing row carries a tick instead, so the two kinds read apart in
 * greyscale as well as in colour.
 *
 * **Expanding a row shows its arithmetic with this dataset's real weights** — the dimension's share of
 * the 100 points, this check's share of the dimension, and the pass rate — because "why did this cost
 * 7.92" is answered by three multiplications, and a reader who can redo them trusts the number.
 *
 * The two actions are addressed by the rule rather than by its version, so a factor whose version
 * could not be read offers neither: there is no rule to open.
 */

export interface ScoreFactorListProps {
  readonly factors: ScoreFactors;
  readonly datasetId: string;
  readonly filter: FactorFilter;
  readonly onFilter: (filter: FactorFilter) => void;
  readonly showAll: boolean;
  readonly onShowAll: (showAll: boolean) => void;
  readonly open: string | null;
  readonly onOpen: (ruleVersionId: string | null) => void;
}

/* Tone a costly check's impact; a passing check's impact is how much it holds up, which is good news
 * and must not wear the colour that means trouble. Total over `FactorImpact`. */
const COSTLY_TONE = {
  high: 'danger',
  medium: 'warn',
  low: 'neutral',
  none: 'neutral',
} as const satisfies Record<FactorImpact, BadgeTone>;

export function ScoreFactorList({
  factors,
  datasetId,
  filter,
  onFilter,
  showAll,
  onShowAll,
  open,
  onOpen,
}: ScoreFactorListProps): ReactElement {
  const { t } = useTranslation();
  const rules = factors.rules;
  const ranks = fixRanks(rules);
  const costlyCount = hurting(rules).length;
  const scale = largestSlice(rules);
  const pool = filterFactors(rules, filter);
  const searching = filter.query.trim() !== '';
  const shown = showAll || searching ? pool : pool.slice(0, FACTORS_SHOWN);
  const hidden = pool.slice(shown.length);
  const hiddenLost = hidden.reduce((sum, rule) => sum + rule.points_lost, 0);
  const dimensionName =
    filter.dimension === null ? '' : t(`scoring.dimension.${filter.dimension}`);

  const views: {
    readonly id: FactorView;
    readonly label: string;
    readonly count: number;
  }[] = [
    { id: 'hurting', label: t('dataset.score.factors.view.hurting'), count: costlyCount },
    {
      id: 'helping',
      label: t('dataset.score.factors.view.helping'),
      count: helping(rules).length,
    },
    { id: 'all', label: t('dataset.score.factors.view.all'), count: rules.length },
  ];

  return (
    <article className="lq-sf__card">
      <header className="lq-sf__cardHead">
        <span className="lq-sf__cardTitles">
          <HelpLabel
            subject={t('dataset.score.factors.title')}
            help={t('help.term.pointsLost')}
          >
            <h2 className="lq-sf__cardTitle">{t('dataset.score.factors.title')}</h2>
          </HelpLabel>
          <span className="lq-sf__cardLead">{t('dataset.score.factors.lead')}</span>
        </span>
        <SegmentedFilter
          variant="track"
          label={t('dataset.score.factors.views')}
          value={filter.view}
          options={views}
          onChange={(view) => {
            onFilter({ ...filter, view });
            onShowAll(false);
          }}
        />
      </header>

      <div className="lq-sf__listTools">
        <span className="lq-sf__listCap">
          {t(`dataset.score.factors.cap.${filter.view}`)}
        </span>
        {filter.dimension === null ? null : (
          <button
            type="button"
            className="lq-sf__dimChip"
            title={t('dataset.score.factors.clearDimension')}
            onClick={() => {
              onFilter({ ...filter, dimension: null });
            }}
          >
            {t('dataset.score.factors.onlyDimension', { dimension: dimensionName })}
            <span aria-hidden="true">×</span>
          </button>
        )}
        {rules.length > SEARCH_FROM ? (
          <input
            type="search"
            className="lq-input lq-sf__search"
            value={filter.query}
            placeholder={t('dataset.score.factors.search')}
            aria-label={t('dataset.score.factors.search')}
            onChange={(event) => {
              onFilter({ ...filter, query: event.target.value });
            }}
          />
        ) : null}
      </div>

      {shown.length === 0 ? (
        <p className="lq-sf__empty">
          {searching
            ? t('dataset.score.factors.empty.query', { query: filter.query.trim() })
            : filter.view === 'hurting'
              ? filter.dimension === null
                ? t('dataset.score.factors.empty.hurting')
                : t('dataset.score.factors.empty.hurtingIn', { dimension: dimensionName })
              : filter.view === 'helping' && helping(rules).length === 0
                ? t('dataset.score.factors.empty.helping')
                : t('dataset.score.factors.empty.group')}
        </p>
      ) : (
        <ul className="lq-sf__rows">
          {shown.map((rule) => (
            <FactorRow
              key={rule.rule_version_id}
              rule={rule}
              factors={factors}
              datasetId={datasetId}
              rank={ranks.get(rule.rule_version_id) ?? null}
              costlyCount={costlyCount}
              scale={scale}
              open={open === rule.rule_version_id}
              onToggle={() => {
                onOpen(open === rule.rule_version_id ? null : rule.rule_version_id);
              }}
            />
          ))}
        </ul>
      )}

      {hidden.length > 0 || (showAll && pool.length > FACTORS_SHOWN && !searching) ? (
        <div className="lq-sf__listFoot">
          {hidden.length > 0 ? (
            <button
              type="button"
              className="lq-btn lq-btn--secondary lq-btn--compact"
              onClick={() => {
                onShowAll(true);
              }}
            >
              {hidden.every((rule) => rule.direction === 'negative')
                ? t('dataset.score.factors.moreLost', {
                    count: hidden.length,
                    points: points(hiddenLost),
                  })
                : t('dataset.score.factors.more', { count: hidden.length })}
            </button>
          ) : (
            <button
              type="button"
              className="lq-sf__fewer"
              onClick={() => {
                onShowAll(false);
              }}
            >
              {t('dataset.score.factors.fewer')}
            </button>
          )}
        </div>
      ) : null}
    </article>
  );
}

function FactorRow({
  rule,
  factors,
  datasetId,
  rank,
  costlyCount,
  scale,
  open,
  onToggle,
}: {
  readonly rule: RuleFactor;
  readonly factors: ScoreFactors;
  readonly datasetId: string;
  readonly rank: number | null;
  readonly costlyCount: number;
  readonly scale: number;
  readonly open: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const bodyId = useId();
  const costly = rule.direction === 'negative';
  const list = new Intl.ListFormat(i18n.language, { type: 'conjunction' });
  const check = checkText(t, checkSentence(rule.check), list);
  const line = passLine(rule);
  const value = factors.value;

  return (
    <li className="lq-sf__row" data-open={open ? 'true' : undefined}>
      <button
        type="button"
        className="lq-sf__rowHead"
        aria-expanded={open}
        aria-controls={bodyId}
        title={t('dataset.score.factors.expand')}
        onClick={onToggle}
      >
        <span
          className="lq-sf__rank"
          data-direction={rule.direction}
          aria-label={
            costly && rank !== null
              ? t('dataset.score.factors.rank', { rank, total: costlyCount })
              : t('dataset.score.factors.kept')
          }
        >
          {costly && rank !== null ? rank : '✓'}
        </span>

        <span className="lq-sf__rowMain">
          <span className="lq-sf__rowTitle">
            <span className="lq-sf__ruleName">{rule.rule_name}</span>
            <span className="lq-sf__ruleVersion">
              {t('dataset.score.factors.version', { label: rule.rule_label })}
            </span>
            <span className="lq-sf__dimTag">
              {t(`scoring.dimension.${rule.dimension}`)}
            </span>
            <span className="lq-sf__severity">
              {t('dataset.score.factors.severity', {
                severity: rule.severity,
                weight: rule.severity_weight,
              })}
            </span>
          </span>
          <span
            className="lq-sf__check"
            data-unreadable={rule.check === null ? 'true' : undefined}
          >
            {rule.check === null ? (
              <span className="lq-sf__unreadableDot" aria-hidden="true" />
            ) : null}
            <span>{check.text}</span>
            {check.code === null ? null : (
              <code className="lq-sf__code">{check.code}</code>
            )}
          </span>
          <span className="lq-sf__measure">
            <span
              className="lq-sf__meter"
              role="img"
              aria-label={t('dataset.score.factors.meter', {
                kept: points(rule.points_earned),
                available: points(rule.points_available),
              })}
              style={
                {
                  '--sf-slice': `${(scale > 0 ? (rule.points_available / scale) * 100 : 0).toFixed(1)}%`,
                  '--sf-kept': `${(rule.pass_ratio * 100).toFixed(2)}%`,
                } as CSSProperties
              }
            >
              <span className="lq-sf__meterSlice">
                <span className="lq-sf__meterKept" />
              </span>
            </span>
            <span className="lq-sf__passLine">
              {line.kind === 'table'
                ? t('dataset.score.factors.pass.table', {
                    passed: line.passed ? 'yes' : 'no',
                  })
                : line.kind === 'allRows'
                  ? t('dataset.score.factors.pass.allRows', { rows: line.rows })
                  : t('dataset.score.factors.pass.someRows', {
                      pass: percent(line.pass),
                      failed: line.failed,
                      rows: line.rows,
                    })}
            </span>
          </span>
        </span>

        <span className="lq-sf__rowFigure">
          <span className="lq-sf__rowPoints">
            <span className="lq-sf__points" data-direction={rule.direction}>
              {costly ? `−${points(rule.points_lost)}` : points(rule.points_earned)}
            </span>
            <span className="lq-sf__pointsOf">
              {t('dataset.score.factors.of', {
                available: points(rule.points_available),
              })}
            </span>
          </span>
          <span className="lq-sf__pointsCap">
            {costly
              ? t('dataset.score.factors.lostCap')
              : t('dataset.score.factors.earnedCap')}
          </span>
          {rule.impact === 'none' ? null : (
            <StatusBadge
              tone={costly ? COSTLY_TONE[rule.impact] : 'ok'}
              label={t(`dataset.score.factors.impact.${rule.impact}`)}
            />
          )}
        </span>
        <span className="lq-sf__chevron" aria-hidden="true">
          ›
        </span>
      </button>

      {open ? (
        <div id={bodyId} className="lq-sf__rowBody">
          <h3 className="lq-sf__howTitle">{t('dataset.score.factors.howTitle')}</h3>
          <ol className="lq-sf__steps">
            {stepsText(t, arithmetic(factors, rule), list).map((step) => (
              <li key={step} className="lq-sf__step">
                {step}
              </li>
            ))}
          </ol>
          {costly && value !== null ? (
            <p className="lq-sf__ifPassed">
              {t('dataset.score.factors.ifPassed', {
                perTable: rule.check?.scope === 'table' ? 'yes' : 'no',
                to: points(Math.min(100, value + rule.points_lost)),
                from: points(value),
              })}
            </p>
          ) : null}
          {rule.rule_id === null ? null : (
            <div className="lq-sf__actions">
              <LatestViolationFix
                ruleId={rule.rule_id}
                datasetId={datasetId}
                className="lq-btn lq-btn--primary lq-btn--compact"
                label={t('dataset.score.factors.violations')}
              />
              <Link
                className="lq-btn lq-btn--secondary lq-btn--compact"
                to={`${SCREEN_PATHS.rules}/${rule.rule_id}`}
              >
                {t('dataset.score.factors.openRule')}
              </Link>
            </div>
          )}
        </div>
      ) : null}
    </li>
  );
}

interface CheckText {
  readonly text: string;
  /** A pattern or a parent column, shown as code beside the sentence because it is the check. */
  readonly code: string | null;
}

function checkText(
  t: TFunction,
  sentence: CheckSentence,
  list: Intl.ListFormat,
): CheckText {
  const plain = (text: string): CheckText => ({ text, code: null });
  if (sentence.kind === 'unreadable') return plain(t('dataset.score.check.unreadable'));
  if (sentence.kind === 'notEmpty') return plain(t('dataset.score.check.notEmpty'));
  const columns = list.format(sentence.columns);
  const count = sentence.columns.length;
  switch (sentence.kind) {
    case 'notNull':
      return plain(t('dataset.score.check.notNull', { columns, count }));
    case 'unique':
      return plain(t('dataset.score.check.unique', { columns, count }));
    case 'acceptedValues':
      return plain(
        sentence.allowed === null
          ? t('dataset.score.check.acceptedValuesUnknown', { columns })
          : t('dataset.score.check.acceptedValues', {
              columns,
              allowed: sentence.allowed,
            }),
      );
    case 'range':
      return plain(
        sentence.min !== null && sentence.max !== null
          ? t('dataset.score.check.rangeBoth', {
              columns,
              min: sentence.min,
              max: sentence.max,
            })
          : sentence.min !== null
            ? t('dataset.score.check.rangeMin', { columns, min: sentence.min })
            : sentence.max !== null
              ? t('dataset.score.check.rangeMax', { columns, max: sentence.max })
              : t('dataset.score.check.rangeNone', { columns }),
      );
    case 'pattern':
      return sentence.pattern === null
        ? plain(t('dataset.score.check.patternUnknown', { columns }))
        : { text: t('dataset.score.check.pattern', { columns }), code: sentence.pattern };
    case 'freshness': {
      if (sentence.limit === null)
        return plain(t('dataset.score.check.freshnessUnknown'));
      const age = t(`dataset.score.check.${sentence.limit.unit}`, {
        count: sentence.limit.count,
      });
      return plain(
        count === 0
          ? t('dataset.score.check.freshnessData', { age })
          : t('dataset.score.check.freshnessColumns', { columns, count, age }),
      );
    }
    case 'reference':
      return sentence.reference === null
        ? plain(t('dataset.score.check.referenceUnknown', { columns }))
        : {
            text: t('dataset.score.check.reference', { columns }),
            code: sentence.reference,
          };
    case 'other':
      return plain(
        count === 0
          ? t('dataset.score.check.other', { name: sentence.name })
          : t('dataset.score.check.otherOn', { name: sentence.name, columns }),
      );
  }
}

function stepsText(
  t: TFunction,
  sums: Arithmetic,
  list: Intl.ListFormat,
): readonly string[] {
  const dimension = t(`scoring.dimension.${sums.dimension}`);
  const severity = (name: string): string => t(`scoring.severity.${name}`);
  const first = t('dataset.score.factors.step.dimension', {
    dimension,
    points: points(sums.dimensionPoints),
  });
  const reweighted =
    sums.reweighted === null
      ? ''
      : ` ${t('dataset.score.factors.step.reweighted', {
          configured: percent(sums.reweighted.configured, 0),
          share: percent(sums.reweighted.share),
          reasons: list.format(
            sums.reweighted.reasons.map((reason) =>
              t('dataset.score.factors.step.reason', {
                dimension: t(`scoring.dimension.${reason.dimension}`),
                state: reason.state,
              }),
            ),
          ),
          count: sums.reweighted.reasons.length,
        })}`;
  const peerCount = sums.peers.reduce((total, group) => total + group.count, 0);
  const second =
    peerCount > 1
      ? t('dataset.score.factors.step.peers', {
          dimension,
          severity: severity(sums.severity),
          weight: sums.severityWeight,
          total: sums.peerWeight,
          parts: sums.peers
            .map((group) =>
              t('dataset.score.factors.step.part', {
                count: group.count,
                severity: severity(group.severity),
                weight: group.weight,
              }),
            )
            .join(' + '),
          share: percent(sums.shareOfDimension),
          points: points(sums.available),
        })
      : t('dataset.score.factors.step.only', {
          dimension,
          points: points(sums.available),
        });
  const third = t('dataset.score.factors.step.pass', {
    pass: percent(sums.pass),
    perTable: sums.perTable ? 'yes' : 'no',
    available: points(sums.available),
    earned: points(sums.earned),
    lost: points(sums.lost),
  });
  return [`${first}${reweighted}`, second, third];
}
